import { z } from '../zod.js';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { PaginatedListSchema } from '../shared/paginatedList';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId.js';
import { SupportedCurrencyEnum, CouponDurationEnum } from './enum.js';
import {
  CouponRuleResponseValidationSchema,
  couponPageIdsDescription,
  couponRulesDescription,
  couponTicketTypeIdsDescription,
} from './scopeDescriptions.js';

const labelOpenapi = {
  description: 'Internal name for the coupon.',
  example: 'Free shipping within US',
};

const couponOpenapi = {
  description: 'The coupon code a customer enters.',
  example: '10Off',
};

const deletedOpenapi = {
  description:
    'If true, this coupon will be soft deleted. It is no longer usable but data is retained.',
  example: false,
};

export const CouponResponseValidationSchema = z
  .object({
    id: z.string().meta({ description: 'Unique identifier for the object.' }),
    label: z.string().meta(labelOpenapi),
    code: z.string().meta(couponOpenapi),
    amountOff: z
      .number()
      .meta({
        description:
          'Flat discount amount in the specified `currency`. Only set if `percentOff` is not used.',
      })
      .nullable()
      .optional(),
    currency: SupportedCurrencyEnum.meta({
      description:
        'Three-letter ISO code for the `currency` (required when `amountOff` is set). Examples: usd, eur, gbp.',
    }).optional(),
    percentOff: z
      .number()
      .meta({
        description:
          'Percentage discount (0-100) applied to payments using this coupon. Example: 50 applies 50% off ($100 payment becomes $50). Only set if `amountOff` is not used.',
      })
      .nullable()
      .optional(),
    appliesToSetupFee: z
      .boolean()
      .meta({
        description: 'When enabled, applies this coupon discount to the setup fee.',
      })
      .optional(),
    appliesToTicketFees: z
      .boolean()
      .meta({
        description:
          'When enabled, applies this coupon discount to ticket-level fees on event pages as well as the ticket prices.',
      })
      .optional(),

    duration: CouponDurationEnum,
    durationInMonths: z
      .number()
      .meta({
        description:
          'If `duration` is `repeating`, the number of months the coupon applies. Null if coupon `duration` is forever or once.',
      })
      .optional(),
    redeemBy: z.iso
      .datetime()
      .meta({
        description: 'The ISO datetime that this coupon must be used before.',
      })
      .optional(),
    maxRedemptions: z
      .number()
      .meta({
        description:
          'Maximum number of times this coupon can be redeemed globally. A single customer may redeem it multiple times.',
      })
      .optional(),
    timesRedeemed: z
      .number()
      .meta({ description: 'The number of times this coupon has been redeemed.' }),
    deleted: z.boolean().meta(deletedOpenapi),
    sellerId: z.string(),
    rules: z
      .array(CouponRuleResponseValidationSchema)
      .optional()
      .meta({ description: couponRulesDescription }),
    pageIds: z
      .array(z.string())
      .optional()
      .meta({ description: couponPageIdsDescription, deprecated: true }),
    ticketTypeIds: z
      .array(ObjectIdStringValidationSchema)
      .optional()
      .meta({
        description: couponTicketTypeIdsDescription,
        deprecated: true,
        example: ['67ee075004de439ab0b675b6', '67fcbdac6a91c25ef2d3534a'],
      }),
    stripeCouponId: z
      .string()
      .meta({
        description:
          'The unique identifier of the stripe coupon that was created with this coupon.',
      })
      .optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'Coupon' });

export const CreateCouponResponseValidationSchema = z.object({
  data: CouponResponseValidationSchema,
});

export const CouponToolDataValidationSchema = CouponResponseValidationSchema.extend({
  dashboardUrl: DashboardUrlField,
});

export const CouponDetailToolResponseValidationSchema = z.object({
  data: CouponToolDataValidationSchema,
});

export const CouponsToolResponseValidationSchema = PaginatedListSchema(
  CouponToolDataValidationSchema,
);
